export type ParsedTableUrl = {
  workspaceId: string | null;
  /** When set, “กลับหน้าหลัก” targets `/?workspaceId=…&serviceId=…&source=order`. */
  serviceId: string | null;
  workerId: string;
  highlight: string | null;
  /** Alias to `highlight` for newer deep-links. */
  recordId: string | null;
  /** Active tab inside drawer modal. */
  drawerTab: "details" | "history" | null;
  /** Sub-modal within drawer (e.g. receivable match/corrections). */
  modal: string | null;
  initialFilters: Record<string, string>;
};

/** Home deep-link for order/workspace context (matches main app query shape). */
export function buildBackToOrderHref(
  workspaceId: string | null,
  serviceId: string | null,
): string {
  if (!workspaceId?.trim()) return "/";
  const params = new URLSearchParams();
  params.set("workspaceId", workspaceId.trim());
  if (serviceId?.trim()) params.set("serviceId", serviceId.trim());
  params.set("source", "order");
  return `/?${params.toString()}`;
}

/**
 * Allow only same-origin relative paths for `returnTo` (open-redirect safe).
 * Accepts `/path?query` — rejects protocol-relative `//…` and absolute URLs.
 */
export function sanitizeInternalReturnTo(
  raw: string | null | undefined,
  fallback: string,
): string {
  const s = String(raw ?? "").trim();
  if (s.startsWith("/") && !s.startsWith("//")) return s;
  return fallback;
}

/** `search` may be `window.location.search`, `useSearchParams().toString()`, or a leading `?` is fine. */
export function parseTableUrl(search: string): ParsedTableUrl {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const workspaceId = params.get("workspaceId");
  const serviceId = params.get("serviceId");
  const workerId = params.get("workerId") ?? "bill-lin-income";
  const recordId = params.get("recordId");
  const highlight = params.get("highlight") ?? recordId;
  const drawerTabRaw = params.get("drawerTab");
  const drawerTab =
    drawerTabRaw === "details" || drawerTabRaw === "history"
      ? drawerTabRaw
      : null;
  const modal = params.get("modal");

  const initialFilters: Record<string, string> = {};
  params.forEach((value, key) => {
    if (key.startsWith("filter_") && value !== "") {
      initialFilters[key.slice(7)] = value;
    }
  });

  // Legacy Billin deep-links / date-filter sticky URLs used recorded_at or
  // record_date. Income/expense lists now order by "ล่าสุด" (= updated_at).
  const isBillinLedger =
    workerId === "bill-lin-income" || workerId === "bill-lin-expense";
  if (
    initialFilters.sort === "recorded_at" ||
    (isBillinLedger && initialFilters.sort === "record_date")
  ) {
    initialFilters.sort = "updated_at";
  }

  return {
    workspaceId,
    serviceId,
    workerId,
    highlight,
    recordId,
    drawerTab,
    modal,
    initialFilters,
  };
}
